import { useEffect } from 'react';
import { useBrandSettings } from './useBrandSettings';

// Convert HEX to HSL
const hexToHSL = (hex: string): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
};

export const useDynamicBranding = () => {
  const { data: brandSettings } = useBrandSettings();

  useEffect(() => {
    if (brandSettings) {
      // Update page title
      if (brandSettings.store_name) {
        document.title = brandSettings.store_name;
      }

      // Update favicon with cache busting
      if (brandSettings.favicon_url) {
        let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
        
        // If favicon link doesn't exist, create it
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          favicon.id = 'favicon-link';
          document.head.appendChild(favicon);
        }
        
        // Add timestamp to bust cache
        const faviconUrl = brandSettings.favicon_url.includes('?') 
          ? `${brandSettings.favicon_url}&t=${Date.now()}`
          : `${brandSettings.favicon_url}?t=${Date.now()}`;
        
        favicon.href = faviconUrl;
        
        // Also update the type based on file extension
        const ext = brandSettings.favicon_url.split('.').pop()?.toLowerCase();
        if (ext === 'png') {
          favicon.type = 'image/png';
        } else if (ext === 'svg') {
          favicon.type = 'image/svg+xml';
        } else if (ext === 'ico') {
          favicon.type = 'image/x-icon';
        } else {
          favicon.type = 'image/x-icon';
        }
      }

      // Update theme colors dynamically
      if (brandSettings.primary_color) {
        const primaryHSL = hexToHSL(brandSettings.primary_color);
        document.documentElement.style.setProperty('--primary', primaryHSL);
        document.documentElement.style.setProperty('--ring', primaryHSL);
        document.documentElement.style.setProperty('--success', primaryHSL);
        
        // Update sidebar colors
        document.documentElement.style.setProperty('--sidebar-background', primaryHSL);
        document.documentElement.style.setProperty('--sidebar-primary-foreground', primaryHSL);
        
        // Update gradients with primary color
        const [h, s, l] = primaryHSL.split(' ');
        const lighterL = Math.min(parseInt(l) + 10, 100);
        document.documentElement.style.setProperty(
          '--gradient-primary', 
          `linear-gradient(135deg, hsl(${h} ${s} ${l}) 0%, hsl(${h} ${s} ${lighterL}%) 100%)`
        );
        document.documentElement.style.setProperty(
          '--gradient-success', 
          `linear-gradient(135deg, hsl(${h} ${s} ${l}) 0%, hsl(${h} ${s} ${lighterL}%) 100%)`
        );
        document.documentElement.style.setProperty(
          '--shadow-colored',
          `0 4px 20px -2px hsl(${h} ${s} ${l} / 0.2)`
        );
      }

      if (brandSettings.secondary_color) {
        const secondaryHSL = hexToHSL(brandSettings.secondary_color);
        document.documentElement.style.setProperty('--sidebar-accent', secondaryHSL);
      }

      if (brandSettings.accent_color) {
        const accentHSL = hexToHSL(brandSettings.accent_color);
        document.documentElement.style.setProperty('--accent', accentHSL);
      }
    }
  }, [brandSettings]);

  return brandSettings;
};
